import { useEffect, useState } from "react";
import { FormControl, FormHelperText, TextField } from "@mui/material";
import { useAppDispatch } from "@redux/hooks";
import { notifyUser } from "@redux/slices/CommonSlice";
import { EmailRegex } from "@utils/regex";
import { useForgotPasswordOtpMutation } from "../authApi";
import {
  LoginInfoIcon,
  LoginMailIcon,
  YggLogo,
} from "@features/common/SvgIcons";
import Button from "@features/common/button/Button";
import WarningBlock from "@features/common/warningBlock/WarningBlock";
import HelpText from "../helpText/HelpText";
import FbWarning from "../fbWarning/FbWarning";
import styles from "../loginForm/LoginForm.module.scss";

interface ForgotPasswordProps {
  onEmailIdVerified: () => void;
  setOtpUrls: (urls: any) => void;
}

const ForgotPassword = ({
  onEmailIdVerified,
  setOtpUrls,
}: ForgotPasswordProps) => {
  const dispatch = useAppDispatch();

  // State
  const [email, setEmail] = useState("");
  const [emailHelperText, setEmailHelperText] = useState("");
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [showFbWarning, setShowFbWarning] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState("");

  // API mutation for requesting OTP
  const [
    getForgotPasswordOtp,
    {
      data: forgotPasswordOtpData,
      error: forgotPasswordOtpError,
      isSuccess: forgotPasswordOtpIsSuccess,
      isLoading: forgotPasswordOtpLoading,
    },
  ] = useForgotPasswordOtpMutation();

  // Validate email format
  const validateForm = () => {
    let isValid = false;
    if (!EmailRegex.test(email)) {
      setEmailHelperText("Invalid Email");
    } else isValid = true;
    return isValid;
  };

  const clearFormErrors = () => {
    setEmailHelperText("");
  };

  const handleFBLogin = () => {
    setShowFbWarning(true);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearFormErrors();
    const isValidForm = validateForm();
    if (isValidForm) {
      try {
        getForgotPasswordOtp({ email: email });
      } catch (err) {}
    }
  };

  // Pre-fill email from URL parameter if present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get("email");
    const emailDecoded = decodeURIComponent(emailParam || "");

    if (emailParam) {
      setEmail(emailDecoded);
    }
  }, []);

  // Enable/disable submit button based on email input
  useEffect(() => {
    if (email) {
      setIsSubmitDisabled(false);
    } else {
      setIsSubmitDisabled(true);
    }
  }, [email, setIsSubmitDisabled]);

  // Handle OTP API response - on success navigate to OTP screen, on error show validation messages
  useEffect(() => {
    if (forgotPasswordOtpIsSuccess) {
      onEmailIdVerified();
      setOtpUrls(forgotPasswordOtpData?.data?.actions);
      dispatch(notifyUser({ message: forgotPasswordOtpData.message }));
    }
    if (forgotPasswordOtpError) {
      if ((forgotPasswordOtpError as any)?.status === 400) {
        setEmailHelperText(
          (forgotPasswordOtpError as any)?.data?.errors?.email[0]
        );
      } else if ((forgotPasswordOtpError as any)?.status === 429) {
        setEmailHelperText(
          (forgotPasswordOtpError as any)?.data?.errors?.message
        );
      } else {
        setShowFbWarning((forgotPasswordOtpError as any)?.data?.message);
      }
    }
  }, [
    forgotPasswordOtpIsSuccess,
    forgotPasswordOtpData,
    forgotPasswordOtpError,
  ]);

  return (
    <div className={styles["signin-email"]}>
      <div className={styles["header"]}>
        <YggLogo />
        <h5>Forgot Password?</h5>
      </div>

      <p className={styles["description"]}>
        Verify your email ID to reset password
      </p>

      <form className={"email-login"} onSubmit={handleSubmit}>
        <div
          className={`${styles["email-input-button"]} ${
            emailHelperText ? styles["error-outline"] : ""
          }`}
        >
          <div className={`${styles["email-input-button__input"]}`}>
            <LoginMailIcon />
            <FormControl variant="standard" fullWidth={true}>
              <TextField
                autoFocus={true}
                value={email}
                name="email"
                id="email"
                type="text"
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => {
                  setEmailHelperText("");
                  setShowWarning("");
                }}
                variant="standard"
                placeholder="mail@email.com"
                inputProps={{ maxLength: 150 }}
                sx={{
                  "& .MuiInput-underline:before": { borderBottom: "none" },
                  "& .MuiInput-underline:after": { borderBottom: "none" },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottom: "none",
                  },
                  "& .MuiInput-root:before": { borderBottom: "none" },
                  "& .MuiInput-root:after": { borderBottom: "none" },
                  "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                    borderBottom: "none",
                  },
                }}
              />
            </FormControl>
          </div>
          <div
            className={`button-container-email ${styles["button-container-email"]}`}
          >
            <Button
              id="btnSubmit"
              theme="purple"
              className={`submit-button ${styles["email-input-button__button"]}`}
              attribues={{
                disabled:
                  isSubmitDisabled ||
                  forgotPasswordOtpLoading ||
                  showWarning?.length,
              }}
            >
              Continue
            </Button>
          </div>
        </div>
        {showWarning && <WarningBlock message={showWarning} />}

        {emailHelperText && (
          <FormHelperText error={!!emailHelperText} id="email-helper-text">
            {emailHelperText}
          </FormHelperText>
        )}
      </form>
      <div className={styles["trouble-signin-container"]}>
        <div className={styles["trouble-signin"]}>
          <LoginInfoIcon />
          <span className={styles["content"]} onClick={handleFBLogin}>
            Trouble signing in?
          </span>
        </div>
        <HelpText />
      </div>
      <FbWarning open={showFbWarning} onClose={setShowFbWarning} />
    </div>
  );
};

export default ForgotPassword;
